/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import scrollTo from 'utils/scrollTo';
import useGuid from 'effects/useGuid';
import '../Ensemble.scss';
import './Contributors.scss';

/**
 * This is the Contributors component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Contributors({ id, level, heading, list, container }) {
  const guid = useGuid('ensemble-contributors');
  const node = useRef(null);
  const button = useRef(null);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const Header = `h${level}`;

  useEffect(() => {
    function onFocus() {
      if (!node.current.contains(document.activeElement)) {
        setFocus(false);
      }
    }

    if (focus && open) {
      document.body.addEventListener('focusin', onFocus);
    }

    return () => {
      document.body.removeEventListener('focusin', onFocus);
    };
  }, [focus, open]);

  useEffect(() => {
    function onChange() {
      if (`#${id}` === window.location.hash) {
        setOpen(true);
      }
    }

    window.addEventListener('hashchange', onChange);
    onChange();

    return () => {
      window.removeEventListener('hashchange', onChange);
    };
  }, [id]);

  useEffect(() => {
    let timeout = null;
    function onOpen() {
      timeout = setTimeout(() => {
        setOpen(true);
      }, 1000);
    }

    if (container) {
      container.addEventListener('open', onOpen);
    }

    return function cleanup() {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('open', onOpen);
      }
    };
  }, []);

  useEffect(() => {
    if (opened) {
      const showMenu = document.getElementById('show-sub-menu');
      if (showMenu) {
        scrollTo(node.current, {
          alignTop: true,
          offset: showMenu.offsetHeight,
        });
      } else {
        scrollTo(node.current, { alignTop: true, offset: 180 });
      }
    }
  }, [opened]);

  return (
    <section
      ref={node}
      className={classnames('ensemble', 'contributors', { open })}
      onFocus={() => {
        setFocus(true);
      }}
    >
      <button
        id={`button${guid}`}
        ref={button}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`details${guid}`}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        <Header className="h3">
          <span />
          {heading}
        </Header>
      </button>
      <Collapsible
        component="dl"
        id={`details${guid}`}
        role="region"
        aria-labelledby={`button${guid}`}
        active={focus && open}
        open={open}
        onOpen={() => {
          setOpened(true);
        }}
        onClose={() => {
          setOpen(false);
          if (button.current) {
            button.current.focus();
          }
        }}
        onClosed={() => {
          setOpened(false);
        }}
      >
        {list.map(({ id, role, performer }) => (
          <div key={id}>
            <dt>{role}</dt>
            <dd>{performer}</dd>
          </div>
        ))}
      </Collapsible>
    </section>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Contributors.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  level: PropTypes.number.isRequired,
  heading: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      role: PropTypes.string.isRequired,
      performer: PropTypes.string.isRequired,
    })
  ).isRequired,
  container: PropTypes.object,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Contributors.defaultProps = {
  id: null,
  level: 3,
  heading: 'Contributors',
  list: [],
  container: null,
};

export default Contributors;
